import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useExternalContractor } from '@/contexts/ExternalContractorContext'
import { resolveLogoUrl } from '@/lib/resolve-logo'
import { Building2, ChevronDown, Users, Settings, Briefcase, Crown, HardHat, ArrowLeft } from 'lucide-react'

const PLAN_LABELS: Record<string, string> = {
  trial: 'Trial · 14 dias',
  basic: 'Basic',
  pro: 'Pro',
  enterprise: 'Enterprise',
}

export default function CompanyDropdown() {
  const { company, companyLimits, user } = useAuth()
  const { hasExternalObras, externalObras, isExternalMode, activeExternalObra, enterExternalMode, exitExternalMode } = useExternalContractor()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const [resolvedLogo, setResolvedLogo] = useState<string | null>(null)
  const [logoError, setLogoError] = useState(false)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Resolve logo URL
  useEffect(() => {
    setLogoError(false)
    setResolvedLogo(null)
    if (!company?.logo_url) return
    resolveLogoUrl(company.logo_url).then(url => {
      if (url) setResolvedLogo(url)
      else setLogoError(true)
    })
  }, [company?.logo_url])

  if (!company) return null

  const planLabel = PLAN_LABELS[company.plan] || company.plan
  const showLogo = resolvedLogo && !logoError

  return (
    <div ref={ref} className="relative mx-2 mt-2 flex-shrink-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full rounded-xl p-2.5 flex items-center gap-2 transition-all group"
        style={{
          background: open ? 'hsl(var(--amber-bg))' : isExternalMode ? 'hsl(var(--amber-bg))' : 'hsl(var(--bg3))',
          border: `1px solid ${open ? 'hsl(var(--amber) / 0.4)' : isExternalMode ? 'hsl(var(--amber) / 0.3)' : 'hsl(var(--line2))'}`,
        }}
      >
        {isExternalMode ? (
          <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'hsl(var(--amber) / 0.2)' }}>
            <HardHat size={13} style={{ color: 'hsl(var(--amber))' }} />
          </div>
        ) : showLogo ? (
          <img
            src={resolvedLogo}
            alt=""
            className="w-7 h-7 rounded-lg object-cover flex-shrink-0"
            onError={() => setLogoError(true)}
          />
        ) : (
          <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'hsl(var(--amber-bg))' }}>
            <Building2 size={13} style={{ color: 'hsl(var(--amber))' }} />
          </div>
        )}
        <div className="flex-1 min-w-0 text-left">
          <div className="text-[11px] font-bold text-foreground truncate">
            {isExternalMode ? 'Obras como Empreiteiro' : company.name}
          </div>
          <div className="text-[9px] text-muted-foreground font-medium">
            {isExternalMode ? activeExternalObra?.company_name || 'Externo' : planLabel}
          </div>
        </div>
        <ChevronDown size={12} className={`text-muted-foreground flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute top-full left-0 right-0 mt-1 rounded-xl overflow-hidden z-50 animate-fade-up"
          style={{
            background: 'hsl(var(--bg2))',
            border: '1px solid hsl(var(--line2))',
            boxShadow: '0 8px 32px rgba(0,0,0,.5)',
          }}
        >
          {/* Mode switcher */}
          {hasExternalObras && (
            <div className="p-1.5" style={{ borderBottom: '1px solid hsl(var(--line))' }}>
              <div className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider px-2.5 py-1">Contexto</div>
              
              {/* My company button */}
              <button
                onClick={() => { exitExternalMode(); setOpen(false) }}
                className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs font-medium transition-colors ${!isExternalMode ? 'font-bold' : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'}`}
                style={!isExternalMode ? { background: 'hsl(var(--amber-bg))', color: 'hsl(var(--amber))' } : {}}
              >
                <Building2 size={13} />
                <span className="truncate">Minha Empresa</span>
                {!isExternalMode && <span className="ml-auto text-[8px]">●</span>}
              </button>

              {/* External obras */}
              <div className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider px-2.5 pt-2 pb-1">Obras como Empreiteiro</div>
              {externalObras.map(obra => (
                <button
                  key={obra.id}
                  onClick={() => { enterExternalMode(obra); setOpen(false) }}
                  className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs font-medium transition-colors ${activeExternalObra?.id === obra.id ? 'font-bold' : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'}`}
                  style={activeExternalObra?.id === obra.id ? { background: 'hsl(var(--amber-bg))', color: 'hsl(var(--amber))' } : {}}
                >
                  <HardHat size={13} />
                  <div className="flex-1 min-w-0 text-left">
                    <div className="truncate">{obra.nome}</div>
                    <div className="text-[9px] text-muted-foreground truncate">{obra.company_name}</div>
                  </div>
                  {activeExternalObra?.id === obra.id && <span className="ml-auto text-[8px]">●</span>}
                </button>
              ))}
            </div>
          )}

          {/* Company info — only in normal mode */}
          {!isExternalMode && (
            <div className="p-3" style={{ borderBottom: '1px solid hsl(var(--line))' }}>
              <div className="text-xs font-bold text-foreground mb-2">{company.name}</div>
              
              {/* Plan badge */}
              <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold mb-2.5"
                style={{ background: 'hsl(var(--amber-bg))', color: 'hsl(var(--amber))' }}
              >
                <Crown size={9} />
                {planLabel}
              </div>

              {/* Usage bars */}
              {companyLimits && (
                <div className="space-y-2">
                  <UsageBar
                    label="Obras"
                    current={companyLimits.current_projects}
                    max={companyLimits.max_projects}
                    icon={<Briefcase size={10} />}
                  />
                  <UsageBar
                    label="Usuários"
                    current={companyLimits.current_users}
                    max={companyLimits.max_users}
                    icon={<Users size={10} />}
                  />
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          {!isExternalMode && (
            <div className="p-1.5">
              {(user?.company_role === 'owner' || user?.company_role === 'admin') && (
                <>
                  <DropdownBtn icon={<Users size={13} />} label="Gerenciar Equipe" onClick={() => { setOpen(false); navigate('/equipe') }} />
                  <DropdownBtn icon={<Settings size={13} />} label="Configurações" onClick={() => { setOpen(false); navigate('/settings') }} />
                </>
              )}
              {user?.company_role !== 'owner' && user?.company_role !== 'admin' && (
                <DropdownBtn icon={<Settings size={13} />} label="Configurações" onClick={() => { setOpen(false); navigate('/settings') }} />
              )}
            </div>
          )}

          {/* Back to company in external mode */}
          {isExternalMode && (
            <div className="p-1.5">
              <DropdownBtn
                icon={<ArrowLeft size={13} />}
                label="Voltar para Minha Empresa"
                onClick={() => { exitExternalMode(); setOpen(false) }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function UsageBar({ label, current, max, icon }: { label: string; current: number; max: number; icon: React.ReactNode }) {
  const pct = max > 0 ? Math.min((current / max) * 100, 100) : 0
  const danger = pct >= 90
  const warn = pct >= 70

  return (
    <div>
      <div className="flex items-center justify-between mb-0.5">
        <span className="text-[10px] text-muted-foreground flex items-center gap-1">{icon} {label}</span>
        <span className="text-[10px] font-bold" style={{ color: danger ? 'hsl(var(--rose))' : warn ? 'hsl(var(--amber))' : 'hsl(var(--foreground))' }}>
          {current}/{max === 9999 ? '∞' : max}
        </span>
      </div>
      <div className="h-[3px] rounded-full overflow-hidden" style={{ background: 'hsl(var(--line2))' }}>
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: `${pct}%`,
            background: danger ? 'hsl(var(--rose))' : warn ? 'hsl(var(--amber))' : 'hsl(var(--emerald))',
          }}
        />
      </div>
    </div>
  )
}

function DropdownBtn({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground transition-colors hover:bg-secondary/50"
    >
      {icon}
      {label}
    </button>
  )
}
